import { useCallback, useRef, useState } from "react";
import Player from "@vimeo/player";

/**
 * Attaches the Vimeo Player SDK to an existing iframe for seeking/switching.
 *
 * The iframe is rendered directly by React (fast initial load).
 * The SDK connects to it via `new Player(iframe)` for programmatic control.
 * We do NOT call player.destroy() because React owns the iframe element.
 */
export function useVimeoPlayer(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
) {
  const playerRef = useRef<Player | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Attach SDK to the iframe once it's in the DOM
  const attachToIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || playerRef.current) return;

    const player = new Player(iframe);
    playerRef.current = player;

    player.ready().then(() => setIsReady(true)).catch(() => setIsReady(true));
  }, [iframeRef]);

  const seekTo = useCallback((seconds: number) => {
    playerRef.current?.setCurrentTime(seconds).catch(() => {});
  }, []);

  const loadVideo = useCallback((vimeoId: string, startSeconds?: number) => {
    const player = playerRef.current;
    if (!player) return;
    player
      .loadVideo({ id: Number(vimeoId), autoplay: true })
      .then(() => {
        if (startSeconds != null && startSeconds > 0) {
          player.setCurrentTime(startSeconds).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  return { seekTo, loadVideo, isReady, attachToIframe };
}
